import { Link } from "react-router-dom";
import { useSnapshot } from "@/lib/snapshot-context";
import { Icon } from "@/components/Icon";
import type { Service, ServiceState } from "@/lib/types";

type Headline = {
  label: string;
  tone: "ok" | "warn" | "bad";
};

function headlineFor(services: Service[]): Headline {
  if (services.some((s) => s.state === "major-outage")) {
    return { label: "Major outage", tone: "bad" };
  }
  if (services.some((s) => s.state === "degraded")) {
    return { label: "Degraded performance", tone: "warn" };
  }
  if (services.some((s) => s.state === "maintenance")) {
    return { label: "Scheduled maintenance in progress", tone: "warn" };
  }
  return { label: "All systems operational", tone: "ok" };
}

const toneColor: Record<Headline["tone"], string> = {
  ok: "var(--success)",
  warn: "var(--warning)",
  bad: "var(--destructive)",
};

const stateLabel: Record<ServiceState, string> = {
  operational: "Operational",
  degraded: "Degraded",
  "major-outage": "Major outage",
  maintenance: "Maintenance",
};

export function Overview() {
  const { data } = useSnapshot();
  const headline = headlineFor(data.services);
  const activeIncidents = data.incidents.filter(
    (i) => i.status !== "resolved",
  );
  const activeByService = new Map<string, number>();
  for (const i of activeIncidents) {
    activeByService.set(i.serviceId, (activeByService.get(i.serviceId) ?? 0) + 1);
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "40px 24px" }}>
      <section
        style={{
          padding: 24,
          borderRadius: 16,
          border: "1px solid var(--border)",
          background: "var(--card)",
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: toneColor[headline.tone],
            color: "var(--primary-foreground)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name="pulse" size={22} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{headline.label}</div>
          <div style={{ fontSize: 13, color: "var(--muted-foreground)" }}>
            {data.services.length} services · {activeIncidents.length} active{" "}
            {activeIncidents.length === 1 ? "incident" : "incidents"}
          </div>
        </div>
      </section>

      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          border: "1px solid var(--border)",
          borderRadius: 12,
          overflow: "hidden",
          background: "var(--card)",
        }}
      >
        {data.services.map((svc, idx) => {
          const open = activeByService.get(svc.id) ?? 0;
          return (
            <li
              key={svc.id}
              style={{
                borderTop: idx > 0 ? "1px solid var(--border)" : undefined,
              }}
            >
              <Link
                to={`/services/${svc.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 18px",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: toneColor[stateTone(svc.state)],
                  }}
                />
                <span style={{ flex: 1, fontWeight: 500 }}>{svc.name}</span>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                    minWidth: 110,
                    textAlign: "right",
                  }}
                >
                  {stateLabel[svc.state]}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                    minWidth: 90,
                    textAlign: "right",
                  }}
                >
                  {open > 0
                    ? `${open} open ${open === 1 ? "incident" : "incidents"}`
                    : svc.lastChecked}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>

      <div
        style={{
          marginTop: 18,
          textAlign: "right",
          fontSize: 13,
        }}
      >
        <Link
          to="/dashboard"
          style={{ color: "var(--primary)", textDecoration: "underline" }}
        >
          Want more detail? See the full dashboard →
        </Link>
      </div>
    </div>
  );
}

function stateTone(state: ServiceState): Headline["tone"] {
  if (state === "major-outage") return "bad";
  if (state === "degraded" || state === "maintenance") return "warn";
  return "ok";
}
